import React from 'react';
import styled from 'styled-components';
import { Gear } from '@assets/icons/svgs';
import { Body1, Container } from '@ui';
import { useDispatch } from 'react-redux';
import { advancedSettingsActionTypes } from '@constants/actionTypes';
import {
  getAdvancedSettingDisplayName,
  getAdvancedSettingType,
  useAdvancedSettings,
  useAdvancedSettingsFields,
  useAdvancedSettingsInitialization,
  useShowAdvancedSettings,
  useShouldShowAdvancedSettings,
  useAdvancedSettingsLoading,
  getAdvancedSettingKeyName,
} from '@hooks/application/useAdvancedSettings';
import { Modal } from '@ui';
import { CloseButton } from '@ui/buttons/CloseButton';
import { useGptModels } from '@hooks/features/useGptModels';
import { logger } from '@utils/usefulFuncs';
import { ADVANCED_SETTING_VALUE_TYPES } from '@constants/advancedSettings';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import { isUndefined } from '@utils/usefulFuncs';
import { fetchMemoryKeys } from '@services/agentMemory';
import { useIsTornadoEmployee } from '@hooks/user/useCurrentUser';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import DatePicker from 'react-date-picker';

const CalendarIconWrapper = styled.div`
  svg {
    height: 17px;
    width: 17px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
  padding-bottom: 1px;
`;

function OpenCalenderIcon() {
  return (
    <CalendarIconWrapper>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
      </svg>
    </CalendarIconWrapper>
  );
}

const AdvancedSettingsButtonWrapper = styled.div`
  position: absolute;
  top: ${({ Top }) => Top + 16 + 'px'};
  right: 16px;
  z-index: 100;
  cursor: pointer;

  * {
    cursor: pointer;
  }
`;
const AdvancedSettingsWrapper = styled.div`
  padding: 0;

  min-width: 500px;

  @media (max-width: 468px) {
    min-width: 340px;
  }
`;
const TextInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;

  input {
    width: 100%;
  }
`;
const CloseButtonWrapper = styled.div`
  cursor: pointer;

  z-index: 1116;

  * {
    z-index: 1116;
  }

  svg {
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const GearIconWrapper = styled.div`
  svg {
    height: 18px;
    width: 18px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
`;
const Wrapper = styled.div`
  padding: 24px 0 24px 0;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const Label = styled.div`
  padding-bottom: ${({ Bottom }) => (isUndefined(Bottom) ? 8 : Bottom) + 'px'};
  min-width: 200px;

  * {
    font-size: 14px;
    color: ${({ theme }) => theme.themeColors.text};
  }
`;
const BoolDropdownWrapper = styled.div`
  width: 120px;
`;
const CheckboxInput = styled.input`
  cursor: pointer;
  margin-top: 0;
  margin-left: 0px;
  width: 14px !important;
`;
const CheckboxLabel = styled.label`
  cursor: pointer;
  margin: 0;
  user-select: none;
`;
const DatePickerWrapper = styled.div`
  height: 32px;
  padding: 0;
  width: 100%;

  .react-date-picker__inputGroup__input {
    height: 32px !important;
  }
  .react-date-picker__inputGroup {
    padding: 0;
  }
  .react-date-picker__inputGroup__input {
    border: none;
    padding: 0;
  }

  .react-date-picker__inputGroup__input {
    &.react-date-picker__inputGroup__year {
      min-width: 56px;
    }

    min-width: 40px;
    text-align: center;
  }

  .react-date-picker__wrapper {
    border: none;
    border-radius: 4px;
    padding: 0;
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};

    color: ${({ theme }) => theme.themeColors.text};

    .react-calendar__tile--now {
      border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
      background: ${({ theme }) => theme.themeColors.appBackground};
      color: ${({ theme }) => theme.themeColors.buttonText};
    }

    button {
      background: ${({ theme }) => theme.themeColors.appBackground} !important;
      color: ${({ theme }) => theme.themeColors.text} !important;

      &:disabled {
        cursor: not-allowed;
      }

      &:enabled:hover {
        border-radius: 2px;
        background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
        color: ${({ theme }) => theme.themeColors.buttonText} !important;

        * {
          color: ${({ theme }) => theme.themeColors.buttonText} !important;
        }
      }
    }
  }

  .react-calendar__navigation button:disabled {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar__tile--active {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};
    color: ${({ theme }) => theme.themeColors.buttonText};
  }

  .react-calendar__tile:disabled {
    background: ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

function sortItemsByValueType(items) {
  return items.sort((a, b) => {
    const aKeyName = getAdvancedSettingKeyName(a);
    const bKeyName = getAdvancedSettingKeyName(b);

    if (aKeyName === 'model' && bKeyName !== 'model') {
      return -1;
    }
    if (bKeyName === 'model' && aKeyName !== 'model') {
      return 1;
    }

    if (aKeyName < bKeyName) {
      return -1;
    }
    if (aKeyName > bKeyName) {
      return 1;
    }
    return 0;
  });
}

const ModelField = ({ field }) => {
  const { models } = useGptModels();
  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;
  const value = getValue(fieldName);
  const onChange = (e) => setValue(fieldName, e.target.value);
  return (
    <Container top={16}>
      <Label>Model (defaults to recommended version)</Label>
      <Container top={8}>
        <select onChange={onChange} value={value}>
          <option value="">Select Model</option>
          {models.map((m) => (
            <option key={m.key} value={m.id}>
              {m.display_name}
            </option>
          ))}
        </select>
      </Container>
    </Container>
  );
};

const MaxAnswerLengthField = ({ field }) => {
  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;
  const value = getValue(fieldName);

  const onMaxAnswerLengthChangeEffect = (e) => {
    setValue('max_answer_tokens', e.target.value);
    // const maxValue = 4096;
    // const value = e.target.value;
    // const intValue = parseInt(value, 10);
    // if (intValue > maxValue) {
    //   setValue('max_answer_tokens', maxValue.toString());
    // } else {
    // }
  };

  return (
    <TextInputWrapper>
      <Label Bottom={0}>Max Answer Length</Label>
      <input id="max_answer_tokens" value={value} onChange={onMaxAnswerLengthChangeEffect} />
    </TextInputWrapper>
  );
};

const MemoryKeyField = ({ field }) => {
  const isTornadoEmployee = useIsTornadoEmployee();
  const state = useAdvancedSettings();
  const dispatch = useDispatch();
  const [memoryKeys, setMemoryKeys] = React.useState([]);

  React.useEffect(() => {
    if (!isTornadoEmployee) {
      return;
    }

    const cancelRef = { isCanceled: false };
    const loadMemoryKeys = async () => {
      const result = await fetchMemoryKeys();
      if (!cancelRef.isCanceled && Array.isArray(result)) {
        setMemoryKeys(result);
      }
    };

    loadMemoryKeys();

    return () => {
      cancelRef.isCanceled = true;
    };
  }, [isTornadoEmployee]);

  if (!isTornadoEmployee) {
    return null;
  }

  const value = state[field] || '';
  const onChange = (e) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [field]: e.target.value,
      },
    });
  };

  return (
    <Container top={16}>
      <Label>Use Memory</Label>
      <Container top={8}>
        <select onChange={onChange} value={value}>
          <option value="">Select Memory Key</option>
          {memoryKeys.map((memoryKey) => (
            <option key={memoryKey} value={memoryKey}>
              {memoryKey}
            </option>
          ))}
        </select>
      </Container>
    </Container>
  );
};

function formatStringToDate(string) {
  return createTimeInstance(string).toDate();
}

function formatDateToString(date) {
  return formatLocalizedDateTime('api', createTimeInstance(date));
}
const DateSelectionInput = ({ label, value, onChange }) => {
  const [defaultDate] = React.useState(formatDateToString(createTimeInstance().toDate()));
  const [todayDate] = React.useState(createTimeInstance().toDate());

  const handleDateChange = (date) => {
    const nextValue = formatDateToString(date);
    onChange(nextValue);
  };

  React.useEffect(() => {
    if (!value) {
      handleDateChange(defaultDate);
    }
  }, []);

  return (
    <DatePickerWrapper>
      {value ? (
        <DatePicker
          name={label}
          calendarIcon={OpenCalenderIcon}
          onChange={handleDateChange}
          value={formatStringToDate(value)}
          maxDate={todayDate}
        />
      ) : null}
    </DatePickerWrapper>
  );
};

const CUSTOM_FIELDS = {
  model: (props) => <ModelField {...props} />,
  max_answer_tokens: (props) => <MaxAnswerLengthField {...props} />,
  memory_key: (props) => <MemoryKeyField {...props} />,
};

const Field = (props) => {
  const { field } = props;

  const state = useAdvancedSettings();
  const dispatch = useDispatch();

  const setValue = (k, v) => {
    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettings,
      payload: {
        [k]: v,
      },
    });
  };

  const getValue = (k) => {
    return state[k];
  };

  const fieldName = field;

  const customFieldFunc = CUSTOM_FIELDS[fieldName];
  const isCustomField = !!customFieldFunc;
  if (isCustomField) {
    return customFieldFunc(props);
  }

  const fieldLabel = getAdvancedSettingDisplayName(fieldName);
  const fieldType = getAdvancedSettingType(fieldName);

  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.bool) {
    const value = getValue(fieldName);

    const onCheckboxChange = () => {
      setValue(fieldName, !value);
    };
    return (
      <Container top={16} row verticallyCenter>
        <CheckboxLabel htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container>
            <Label Bottom={3}>{fieldLabel}</Label>
          </Container>
        </CheckboxLabel>
        <Container style={{ height: '33px' }} fullWidth verticallyCenter left={8} alignRight rightAlign>
          <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
        </Container>
      </Container>
    );
  }
  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.onOff) {
    let value = getValue(fieldName);
    if (value === undefined) {
      value = '';
    }

    const onBoolChange = (e) => {
      setValue(fieldName, e.target.value);
    };
    return (
      <Container top={16} row verticallyCenter spaceBetween>
        <Label Bottom={0}>{fieldLabel}</Label>
        <BoolDropdownWrapper>
          <select value={value} onChange={onBoolChange}>
            <option value="on">On</option>
            <option value="off">Off</option>
            <option value="auto">Auto</option>
            <option value="">Omitted</option>
          </select>
        </BoolDropdownWrapper>
      </Container>
    );
  }
  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.string) {
    const value = getValue(fieldName);

    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <TextInputWrapper>
        <Label Bottom={0}>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </TextInputWrapper>
    );
  }
  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.integer) {
    const value = getValue(fieldName);
    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <TextInputWrapper>
        <Label Bottom={0}>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </TextInputWrapper>
    );
  }
  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.float) {
    const value = getValue(fieldName);
    const onChange = (e) => {
      const v = e.target.value;
      setValue(fieldName, v);
    };
    return (
      <TextInputWrapper>
        <Label Bottom={0}>{fieldLabel}</Label>
        <input id={fieldName} name={fieldName} value={value} onChange={onChange} />
      </TextInputWrapper>
    );
  }
  if (fieldType === ADVANCED_SETTING_VALUE_TYPES.date) {
    const value = getValue(fieldName);
    const onChange = (v) => {
      setValue(fieldName, v);
    };
    return (
      <Container top={24} row verticallyCenter fullWidth>
        <Label Bottom={0}>{fieldLabel}</Label>
        <Container fullWidth rightAlign alignRight>
          <DateSelectionInput label={fieldLabel} value={value} onChange={onChange} />
        </Container>
      </Container>
    );
  }

  logger('missing field renderer', { field });
  return null;
};

export function AdvancedSettingsButton({ tool, top }) {
  useAdvancedSettingsInitialization(tool);
  const showAdvancedSettings = useShowAdvancedSettings(tool);

  return (
    <AdvancedSettingsButtonWrapper Top={top || 0}>
      <GearIconWrapper onClick={showAdvancedSettings}>
        <Gear />
      </GearIconWrapper>
    </AdvancedSettingsButtonWrapper>
  );
}

export function AdvancedSettingsModal({ onDismiss }) {
  const dispatch = useDispatch();
  const isShowing = useShouldShowAdvancedSettings();
  const isLoading = useAdvancedSettingsLoading();
  const fields = useAdvancedSettingsFields();
  const isTornadoEmployee = useIsTornadoEmployee();

  const [sortedFields, setSortedFields] = React.useState([]);

  React.useEffect(() => {
    if (fields) {
      const employeeOnlyFields = ['memory_key'];
      const visibleFields = fields.filter((field) => isTornadoEmployee || !employeeOnlyFields.includes(field));
      setSortedFields(sortItemsByValueType(visibleFields));
    }
  }, [fields, isTornadoEmployee]);

  const onCloseClick = () => {
    if (onDismiss) {
      onDismiss();
    } else {
      dispatch({ type: advancedSettingsActionTypes.hideAdvancedSettings });
    }
  };

  if (!isShowing) return null;

  return (
    <Modal onDismiss={onCloseClick}>
      <AdvancedSettingsWrapper>
        <Container row verticallyCenter spaceBetween>
          <Container>
            <Body1 bold>Advanced Settings</Body1>
          </Container>
          <CloseButtonWrapper>
            <CloseButton onClick={onCloseClick} />
          </CloseButtonWrapper>
        </Container>
        <Wrapper>
          <FormWrapper>
            <Container>
              {isLoading && sortedFields.length === 0 ? (
                <Container top={16}>
                  <Body1>Loading…</Body1>
                </Container>
              ) : (
                sortedFields.map((field) => <Field key={field} field={field} />)
              )}
            </Container>
          </FormWrapper>
        </Wrapper>
      </AdvancedSettingsWrapper>
    </Modal>
  );
}
